import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  locationSalesGetSalesByEmployee,
  type SalesByEmployeeEntry,
} from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";

type WeeklyEarningsData = SalesByEmployeeEntry;

interface WeeklyEarningsViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const WeeklyEarningsView = ({
  locationId,
  dateRange,
  tableId,
}: WeeklyEarningsViewProps) => {
  const { data, isPending } = useQuery(
    convexQuery(locationSalesGetSalesByEmployee, {
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    })
  );

  const columnHelper = createColumnHelper<WeeklyEarningsData>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("location", {
          header: "Location",
          cell: ({ getValue }) => getValue().name,
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.location.id),
          meta: {
            faceting: {
              title: "Location",
              getOption: (r: WeeklyEarningsData["location"]) => ({
                value: r.id,
                label: r.name,
              }),
            },
            getCellForExport: (r: WeeklyEarningsData["location"]) => r.name,
          },
        }),
        columnHelper.accessor("employee", {
          header: "Employee",
          cell: ({ getValue }) => {
            const employee = getValue();
            return (
              <div className="flex items-center gap-3">
                <img
                  alt={`${employee.firstName} ${employee.lastName}'s avatar`}
                  className="size-6 rounded-full"
                  height={24}
                  src={employee.imageUrl ?? "/images/avatar-placeholder.svg"}
                  width={24}
                />
                <p>
                  {employee.firstName} {employee.lastName}
                </p>
              </div>
            );
          },
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.employee.id),
          meta: {
            getCellForExport: (e: WeeklyEarningsData["employee"]) =>
              `${e.firstName} ${e.lastName}`,
            faceting: {
              title: "Employee",
              getOption: (e: WeeklyEarningsData["employee"]) => ({
                value: e.id,
                label: `${e.firstName} ${e.lastName}`,
              }),
            },
          },
        }),
        columnHelper.accessor("hoursWorked", {
          header: "Hours Worked",
          cell: ({ getValue }) => getValue()?.toFixed(1) || "0.0",
          meta: {
            getCellForExport: (d: WeeklyEarningsData["hoursWorked"]) => d,
          },
        }),
        columnHelper.accessor("amountSold", {
          header: "Amount Sold",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: WeeklyEarningsData["amountSold"]) => d,
          },
        }),
        columnHelper.display({
          id: "estimatedWages",
          header: "Est. Wages (@$15/hr)",
          cell: ({ row }) => {
            const hoursWorked = row.original.hoursWorked || 0;
            const estimatedWages = hoursWorked * 15;
            return estimatedWages.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            });
          },
        }),
        columnHelper.display({
          id: "salesPerHour",
          header: "Sales/Hour",
          cell: ({ row }) => {
            const hoursWorked = row.original.hoursWorked;
            const amountSold = row.original.amountSold;
            if (!hoursWorked || hoursWorked === 0) {
              return "$0.00";
            }
            const salesPerHour = amountSold / hoursWorked;
            return salesPerHour.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            });
          },
        }),
      ] as ColumnDef<WeeklyEarningsData>[],
    [columnHelper]
  );

  if (isPending || !data) {
    return <LoadingPanel message="Loading weekly earnings data..." />;
  }

  return <SalesDataTable columns={columns} data={data} id={tableId} />;
};
